import { getDomain } from "tldts-icann";
import type { EditableWithOverwrites, Overwrite } from "shared-types";

function isValidUrl(str: string): boolean {
    try {
        const url = new URL(str.trim());
        const hasValidProtocol = ["http:", "https:"].includes(url.protocol);
        const hasValidTld = /\.[a-z]{2,}$/i.test(url.hostname);

        return hasValidProtocol && hasValidTld;
    } catch {
        return false;
    }
}

// protocol is optional, e.g. "example.com/offers"
function getLinkHostname(str: string): string | null {
    const input = str.trim();
    const hasProtocol = /^[a-z][a-z\d+.-]*:\/\//i.test(input);

    try {
        const { protocol, hostname } = new URL(hasProtocol ? input : `https://${input}`);
        const isValid = ["http:", "https:"].includes(protocol) && /\.[a-z]{2,}$/i.test(hostname);

        return isValid ? hostname : null;
    } catch {
        return null;
    }
}

type ValidationType = "string" | "displayLink" | "url" | "array";

const validatedFields: Record<string, { type: ValidationType }> = {
    fbCallToAction: { type: "array" },
    fbDescription: { type: "string" },
    fbDisplayLink: { type: "displayLink" },
    fbHeadline: { type: "string" },
    fbPrimaryText: { type: "string" },
    fbWebsiteUrl: { type: "url" }
};

export function getInvalidMetaEditables(
    data: EditableWithOverwrites[],
    editableGroup: { label: string; value: string }
): { label: string; errors: { editableGroup: string; error: string }[] }[] {
    const errorsMap = new Map<string, string>();

    const metaEditables = data.filter(
        ({ editable }) =>
            editable.publisherPlatform === "facebook" && Object.keys(validatedFields).includes(editable.name)
    );

    const getRelevantOverwrites = (overwrites: Overwrite[]) =>
        overwrites.filter(
            o =>
                o.language === "en" &&
                Array.isArray(o.editableGroupValueIds) &&
                o.editableGroupValueIds.includes(editableGroup.value)
        );

    // Meta needs the display link on the website URL root domain, e.g. "bbc.co.uk"
    const websiteUrl = getRelevantOverwrites(
        metaEditables.find(({ editable }) => editable.name === "fbWebsiteUrl")?.overwrites ?? []
    ).find(o => typeof o.value === "string")?.value;
    const websiteDomain =
        typeof websiteUrl === "string" && isValidUrl(websiteUrl)
            ? getDomain(new URL(websiteUrl.trim()).hostname)
            : null;

    metaEditables.forEach(({ editable, overwrites }) => {
        const { name } = editable;
        const validationType = validatedFields[name].type;

        const relevantOverwrites = getRelevantOverwrites(overwrites);

        if (errorsMap.has(name)) {
            return;
        }

        if (relevantOverwrites.length === 0) {
            errorsMap.set(name, "has no value");
            return;
        }

        if (validationType === "string") {
            const hasValue = relevantOverwrites.some(o => typeof o.value === "string" && o.value.trim() !== "");
            if (!hasValue) {
                errorsMap.set(name, "has no value");
            }
        }

        if (validationType === "url") {
            const urlValue = relevantOverwrites.find(o => typeof o.value === "string")?.value;

            if (typeof urlValue !== "string" || urlValue.trim() === "" || !isValidUrl(urlValue)) {
                errorsMap.set(name, "is not a valid link");
            }
        }

        if (validationType === "displayLink") {
            const displayLink = relevantOverwrites.find(o => typeof o.value === "string")?.value;
            const hostname = typeof displayLink === "string" ? getLinkHostname(displayLink) : null;

            if (!hostname) {
                errorsMap.set(name, "is not a valid display link");
            } else if (websiteDomain && getDomain(hostname) !== websiteDomain) {
                errorsMap.set(name, `must contain the website URL domain (${websiteDomain})`);
            }
        }

        if (validationType === "array") {
            const hasSelected = relevantOverwrites.some(
                o => Array.isArray(o.value) && o.value.some(v => v.selected === true)
            );
            if (!hasSelected) {
                errorsMap.set(name, "has no value selected");
            }
        }
    });

    if (errorsMap.size === 0) {
        return [];
    }

    return [
        {
            label: editableGroup.label,
            errors: Array.from(errorsMap.entries()).map(([editableGroup, error]) => ({
                editableGroup,
                error
            }))
        }
    ];
}
